import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import useRealtimeBookings from "../hooks/useRealtimeBookings";
import BookingList from "../components/BookingList";
import "../styles/OnlineBookings.css";

export default function OnlineBookings() {
  const { user, isAdmin } = useAuth();
  const { bookings, loading, error } = useRealtimeBookings();

  const hasBookings = useMemo(
    () => Array.isArray(bookings) && bookings.length > 0,
    [bookings]
  );

  const handleExportCSV = () => {
    if (!hasBookings) {
      alert("No bookings to export.");
      return;
    }

    const headers = [
      "Booking ID",
      "Name",
      "Service",
      "Date",
      "Time",
      "Status",
      "Amount"
    ];

    const rows = bookings.map((b) => [
      `"${b.id || ""}"`,
      `"${b.name || b.customerName || ""}"`,
      `"${b.service || ""}"`,
      `"${b.date || ""}"`,
      `"${b.time || ""}"`,
      `"${b.status || ""}"`,
      `"${b.amount || ""}"`
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(","))
    ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "bookings_export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Auth guard
  if (!user) {
    return (
      <section className="container page online-bookings-page">
        <p className="muted alert-inline">
          ⚠️ Please log in to view your bookings.
        </p>
      </section>
    );
  }

  return (
    <section className="container page online-bookings-page">
      {/* Header */}
      <motion.div
        className="bookings-header"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h2 className="gradient-text">
          {isAdmin ? "All Bookings (Admin)" : "My Bookings"}
        </h2>
        <p className="subtitle">
          {isAdmin
            ? "✅ You are logged in as an Admin. Manage all customer bookings in real-time."
            : "Track and manage your past & upcoming bookings."}
        </p>
      </motion.div>

      {/* Admin Badge */}
      {isAdmin && (
        <div className="admin-indicator">
          <span>🛡️ Admin Mode Enabled</span>
        </div>
      )}

      {/* Export */}
      {!loading && !error && hasBookings && (
        <div className="export-toolbar">
          <button className="export-btn" onClick={handleExportCSV}>
            📤 Export to CSV
          </button>
        </div>
      )}

      {/* States */}
      {loading && (
        <p className="muted loading-inline">
          Loading bookings...
        </p>
      )}

      {error && (
        <p className="error">
          ❌ Failed to load bookings: {error.message || "Unknown error"}
        </p>
      )}

      {/* List */}
      {!loading && !error && (
        <BookingList
          bookings={bookings}
          isAdmin={isAdmin}
          loading={loading}
          error={error}
        />
      )}
    </section>
  );
}
